import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Phone, Mail, MapPin, CheckCircle, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactForm = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    position: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await axios.post(`${API}/contact`, formData);
      
      // Track conversion in Google Analytics
      if (window.trackContactFormSubmit) {
        window.trackContactFormSubmit(formData);
      }
      
      setIsSuccess(true);
      setFormData({
        name: '',
        company: '',
        position: '',
        email: '',
        phone: '',
        message: '',
      });
    } catch (err) {
      setError('Error al enviar el mensaje. Por favor intente nuevamente.');
      console.error('Contact form error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-slate-900"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left - Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-xs font-medium tracking-[0.2em] uppercase text-blue-400 mb-4 block" data-cms="contact.label">
              {t('contact.title')}
            </span>
            <h2 className="text-4xl md:text-5xl font-medium tracking-tight text-white mb-6" data-cms="contact.title">
              {t('contact.subtitle')}
            </h2>

            <p className="text-lg text-slate-400 leading-relaxed mb-10" data-cms="contact.description">
              Agende una sesión estratégica privada con nuestro equipo ejecutivo. 
              Analizaremos su caso y le mostraremos cómo ALLCOM IA puede transformar su organización.
            </p>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-slate-800 flex items-center justify-center rounded-lg">
                  <Phone className="w-5 h-5 text-slate-400" />
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">TELÉFONO</div>
                  <a href="tel:+56975294440" className="text-white font-medium hover:text-blue-400 transition-colors">
                    +56 9 7529 4440
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-600 flex items-center justify-center rounded-lg">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">WHATSAPP</div>
                  <a 
                    href="https://wa.me/56975294440?text=Hola,%20me%20interesa%20conocer%20más%20sobre%20sus%20servicios%20de%20IA" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white font-medium hover:text-green-400 transition-colors"
                  >
                    Escríbenos directo
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-slate-800 flex items-center justify-center rounded-lg">
                  <MapPin className="w-5 h-5 text-slate-400" />
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">UBICACIÓN</div>
                  <div className="text-white font-medium">Santiago, Chile</div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right - Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {isSuccess ? (
              <div className="bg-slate-800 border border-slate-700 rounded-2xl p-12 text-center">
                <div className="w-16 h-16 bg-emerald-500/20 flex items-center justify-center mx-auto mb-6 rounded-full">
                  <CheckCircle className="w-8 h-8 text-emerald-400" />
                </div>
                <h3 className="text-xl font-medium text-white mb-4">
                  Mensaje Enviado
                </h3>
                <p className="text-slate-400">
                  {t('contact.success')}
                </p>
                <Button
                  data-testid="contact-reset-btn"
                  onClick={() => setIsSuccess(false)}
                  variant="outline"
                  className="mt-6 border-slate-600 text-white hover:bg-slate-700"
                >
                  Enviar otro mensaje
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6 bg-slate-800/50 border border-slate-700/50 rounded-2xl p-8" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-xs font-medium tracking-wider uppercase text-slate-400">
                      {t('contact.form.name')}
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      data-testid="contact-name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="h-12 rounded-lg bg-slate-900 border-slate-700 text-white focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-xs font-medium tracking-wider uppercase text-slate-400">
                      {t('contact.form.company')}
                    </Label>
                    <Input
                      id="company"
                      name="company"
                      data-testid="contact-company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      className="h-12 rounded-lg bg-slate-900 border-slate-700 text-white focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="position" className="text-xs font-medium tracking-wider uppercase text-slate-400">
                      {t('contact.form.position')}
                    </Label>
                    <Input
                      id="position"
                      name="position"
                      data-testid="contact-position"
                      value={formData.position}
                      onChange={handleChange}
                      required
                      className="h-12 rounded-lg bg-slate-900 border-slate-700 text-white focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-xs font-medium tracking-wider uppercase text-slate-400">
                      {t('contact.form.email')}
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      data-testid="contact-email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="h-12 rounded-lg bg-slate-900 border-slate-700 text-white focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-xs font-medium tracking-wider uppercase text-slate-400">
                    {t('contact.form.phone')}
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    data-testid="contact-phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="h-12 rounded-lg bg-slate-900 border-slate-700 text-white focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-xs font-medium tracking-wider uppercase text-slate-400">
                    {t('contact.form.message')}
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    data-testid="contact-message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="rounded-lg bg-slate-900 border-slate-700 text-white focus:border-blue-500 focus:ring-blue-500 resize-none"
                    placeholder="Cuéntenos brevemente sobre su empresa..."
                  />
                </div>

                {error && (
                  <div className="text-red-400 text-sm" data-testid="contact-error">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={isSubmitting}
                  className="w-full bg-white text-slate-900 hover:bg-slate-100 py-4 text-sm font-semibold tracking-wider uppercase rounded-lg"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 w-4 h-4" />
                      {t('contact.form.submit')}
                    </>
                  )}
                </Button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
